import { motion } from "framer-motion";
import { MessageSquare, Headset, LifeBuoy, Target, ClipboardList, ArrowUpRight, Check } from "lucide-react";
import { SERVICES } from "@/data/content";

const ICONS = { MessageSquare, Headset, LifeBuoy, Target, ClipboardList };

// Asymmetric bento spans (12-col grid)
const SPANS = [
  "md:col-span-7",
  "md:col-span-5",
  "md:col-span-5",
  "md:col-span-7",
  "md:col-span-12",
];

const ServiceCard = ({ service, span, index }) => {
  const Icon = ICONS[service.icon];
  const wide = span.includes("12");
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className={`col-span-1 ${span}`}
    >
      <div
        data-testid={`service-card-${service.id}`}
        className="group h-full bg-white border border-[#E5E5E5] p-8 md:p-10 hover:border-[#0A0A0A] transition-colors flex flex-col"
      >
        <div className={`flex items-start justify-between ${wide ? "md:items-center" : ""}`}>
          <div className="w-12 h-12 border border-[#0A0A0A] flex items-center justify-center group-hover:bg-[#002DFF] group-hover:border-[#002DFF] transition-colors">
            <Icon className="w-5 h-5 text-[#0A0A0A] group-hover:text-white transition-colors" strokeWidth={1.5} />
          </div>
          <span className="font-mono-nw text-xs text-[#525252]">{service.n}</span>
        </div>

        <div className={`mt-7 ${wide ? "md:grid md:grid-cols-2 md:gap-10 md:items-start" : ""}`}>
          <div>
            <h3 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl">
              {service.title}
            </h3>
            <p className="mt-3 text-[#525252] leading-relaxed text-base max-w-md">
              {service.desc}
            </p>
          </div>
          <ul className={`mt-6 ${wide ? "md:mt-0" : ""} space-y-2`}>
            {service.points.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm text-[#0A0A0A]">
                <Check className="w-4 h-4 text-[#002DFF] shrink-0" strokeWidth={2.5} />
                {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-auto pt-8">
          <a
            href="#contact"
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A] group-hover:text-[#002DFF] transition-colors"
          >
            Request this service <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </motion.div>
  );
};

export const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-32 bg-[#FAFAFA] border-b border-[#E5E5E5]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="font-mono-nw text-xs tracking-[0.25em] uppercase text-[#002DFF]">
              What we do
            </span>
            <h2 className="mt-4 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Omni services.
              <br />
              One operating partner.
              <br />
              Endless possibilities.
            </h2>
          </div>
          <p className="max-w-sm text-[#525252] leading-relaxed">
            Every engagement is staffed, trained and managed by NovaWorks. You get
            outcomes — not another vendor to babysit.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} service={s} span={SPANS[i]} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
